import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Buildings, MapPin, Phone, Globe, PencilSimple, Trash, CaretRight, ChartBar,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import BusinessFormDialog from "@/components/BusinessFormDialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY_IMG = "https://images.unsplash.com/photo-1533090161767-e6ffed986c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjAzNTl8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwZGVzayUyMHBsYW50fGVufDB8fHx8MTc4NTg2MzYzNXww&ixlib=rb-4.1.0&q=85";

export default function Dashboard() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/businesses");
      setBusinesses(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusinesses([]);
    }
  };

  useEffect(() => { load(); }, []);

  const onSaved = () => load();

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/businesses/${deleting.business_id}`);
      toast.success("Business deleted");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const totals = (businesses || []).reduce(
    (acc, b) => {
      acc.live += b.stats?.live || 0;
      acc.inProgress += b.stats?.in_progress || 0;
      acc.slots += b.stats?.total || 0;
      return acc;
    },
    { live: 0, inProgress: 0, slots: 0 }
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#0055FF]">Dashboard</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mt-2">Your businesses</h1>
            <p className="text-base text-slate-600 mt-2">Manage listings and track citations across 138 US directories.</p>
          </div>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-business-button"
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-11 px-5 group self-start sm:self-auto">
            <Plus size={18} weight="bold" className="mr-1.5" /> Add business
          </Button>
        </div>

        {businesses && businesses.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            <StatCard icon={Buildings} label="Businesses" value={businesses.length} />
            <StatCard icon={ChartBar} label="Live citations" value={totals.live} accent="text-emerald-600" />
            <StatCard icon={ChartBar} label="In progress" value={totals.inProgress} accent="text-blue-600" />
            <StatCard icon={MapPin} label="Directory slots" value={totals.slots} />
          </div>
        )}

        <div className="mt-10">
          {businesses === null ? (
            <div className="grid md:grid-cols-2 gap-6">
              {[0, 1].map((i) => <div key={i} className="h-52 rounded-xl border border-slate-200 animate-pulse bg-slate-50" />)}
            </div>
          ) : businesses.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50/50 py-16 px-6 text-center">
              <img src={EMPTY_IMG} alt="" className="max-w-[220px] mx-auto rounded-xl" />
              <h3 className="font-display text-2xl font-bold text-slate-900 mt-8">No businesses yet</h3>
              <p className="text-slate-600 mt-2 max-w-sm mx-auto">Add your first business to start building citations across the web.</p>
              <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="empty-add-business-button"
                className="mt-6 bg-[#0055FF] hover:bg-[#0044CC] text-white h-11 px-5">
                <Plus size={18} weight="bold" className="mr-1.5" /> Add your first business
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {businesses.map((b) => (
                <BusinessCard key={b.business_id} b={b} navigate={navigate}
                  onEdit={() => { setEditing(b); setDialogOpen(true); }}
                  onDelete={() => setDeleting(b)} />
              ))}
            </div>
          )}
        </div>
      </div>

      <BusinessFormDialog open={dialogOpen} onOpenChange={setDialogOpen} business={editing} onSaved={onSaved} />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this business?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.name}" and all its citation tracking will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-button"
              className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

const StatCard = ({ icon: Icon, label, value, accent = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="flex items-center gap-2 text-slate-400"><Icon weight="duotone" size={18} />
      <span className="text-xs font-bold uppercase tracking-[0.1em]">{label}</span></div>
    <p className={`font-display text-3xl font-black mt-2 ${accent}`}>{value}</p>
  </div>
);

const BusinessCard = ({ b, navigate, onEdit, onDelete }) => {
  const s = b.stats || { progress: 0, live: 0, total: 0 };
  return (
    <div data-testid={`business-card-${b.business_id}`}
      className="group rounded-xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg flex flex-col">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 min-w-0">
          {b.logo_path && (
            <img src={fileUrl(b.logo_path)} alt={`${b.name} logo`} data-testid={`business-logo-${b.business_id}`}
              className="h-11 w-11 rounded-lg border border-slate-200 object-contain bg-white shrink-0" />
          )}
          <div className="min-w-0">
            <h3 className="font-display text-xl font-bold text-slate-900 truncate">{b.name}</h3>
            {b.category && <p className="text-xs font-semibold uppercase tracking-wide text-[#0055FF] mt-1">{b.category}</p>}
          </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onEdit} data-testid={`edit-business-${b.business_id}`} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700"><PencilSimple size={16} /></button>
          <button onClick={onDelete} data-testid={`delete-business-${b.business_id}`} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash size={16} /></button>
        </div>
      </div>

      <div className="mt-4 space-y-1.5 text-sm text-slate-600">
        {(b.city || b.state) && <p className="flex items-center gap-2 truncate"><MapPin size={15} className="text-slate-400 shrink-0" />{[b.city, b.state].filter(Boolean).join(", ")}</p>}
        {b.phone && <p className="flex items-center gap-2 truncate"><Phone size={15} className="text-slate-400 shrink-0" />{b.phone}</p>}
        {b.website && <p className="flex items-center gap-2 truncate"><Globe size={15} className="text-slate-400 shrink-0" />{b.website}</p>}
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-semibold text-slate-700">{s.live} of {s.total} live</span>
          <span className="font-mono text-slate-500">{s.progress}%</span>
        </div>
        <Progress value={s.progress} className="h-2" />
      </div>

      <Button onClick={() => navigate(`/business/${b.business_id}`)} data-testid={`open-business-${b.business_id}`}
        variant="secondary" className="mt-5 w-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-900 hover:text-white group/btn">
        Manage citations <CaretRight size={16} className="ml-1 transition-transform group-hover/btn:translate-x-0.5" />
      </Button>
    </div>
  );
};
